/**
 * Enhanced Session Manager
 * Fixes bugs with session synchronization, state management, and data persistence
 */

import { ChatSession, Message, Role } from '../types';
import { connectionManager } from './connectionManager';

class SessionManager {
    private sessions: Map<string, ChatSession> = new Map();
    private currentSessionId: string | null = null;
    private saveCallbacks: Set<(sessions: ChatSession[]) => void> = new Set();
    private readonly STORAGE_KEY = 'ledger_sessions';
    private readonly BACKUP_KEY = 'ledger_sessions_backup';
    private readonly GUEST_SESSION_KEY = 'ledger_guest_session_id';
    private saveTimeout: NodeJS.Timeout | null = null;
    private isDirty = false;
    private isPruning = false; // Prevent infinite recursion
    private cachedSessionsArray: ChatSession[] | null = null; // Performance cache
    private saveCount = 0; // Track saves for periodic size checks

    // Auto-cleanup settings
    private readonly INACTIVE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (was 30 minutes)
    private readonly MAX_MESSAGES_PER_SESSION = 200; // Limit for performance (was 100)
    private autoCleanupInterval: NodeJS.Timeout | null = null;

    constructor() {
        this.loadSessions();
        this.setupAutoSave();
        this.scheduleGuestSessionCleanup();
        this.setupAutoCleanup();
    }

    /**
     * Load sessions from storage with validation
     */
    private loadSessions(): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const parsed: ChatSession[] = JSON.parse(stored);

                // Validate and hydrate sessions
                const validated = parsed
                    .filter(s => s && s.id && Array.isArray(s.messages))
                    .map(s => this.hydrateSession(s));

                validated.forEach(session => {
                    this.sessions.set(session.id, session);
                });
            }
        } catch (error) {
            console.error('[SessionManager] Failed to load sessions:', error);
            this.loadBackup();
        }
    }

    /**
     * Load backup sessions if main storage fails
     */
    private loadBackup(): void {
        try {
            const backup = localStorage.getItem(this.BACKUP_KEY);
            if (backup) {
                const parsed: ChatSession[] = JSON.parse(backup);
                const validated = parsed
                    .filter(s => s && s.id && Array.isArray(s.messages))
                    .map(s => this.hydrateSession(s));

                validated.forEach(session => {
                    this.sessions.set(session.id, session);
                });

                console.log(`[SessionManager] Restored ${validated.length} sessions from backup`);
                this.saveSessions(); // Save to main storage
            }
        } catch (error) {
            console.error('[SessionManager] Failed to load backup:', error);
        }
    }

    /**
     * Hydrate session data (convert timestamps, validate structure)
     */
    private hydrateSession(session: ChatSession): ChatSession {
        return {
            ...session,
            messages: session.messages.map(m => ({
                ...m,
                timestamp: m.timestamp instanceof Date
                    ? m.timestamp
                    : new Date(m.timestamp || Date.now())
            })),
            createdAt: typeof session.createdAt === 'number'
                ? session.createdAt
                : Date.now(),
            lastModified: typeof session.lastModified === 'number'
                ? session.lastModified
                : Date.now(),
            // Preserve important session data
            pinnedMessages: Array.isArray(session.pinnedMessages) ? session.pinnedMessages : [],
            generatedMaterials: Array.isArray(session.generatedMaterials) ? session.generatedMaterials : [],
            tags: Array.isArray(session.tags) ? session.tags : []
        };
    }

    /**
     * Setup auto-save mechanism with debouncing
     */
    private setupAutoSave(): void {
        // Save every 3 seconds if dirty (reduced from 5s for responsiveness)
        setInterval(() => {
            if (this.isDirty) {
                this.saveSessions();
                this.isDirty = false;
            }
        }, 3000);

        // Save on page unload
        window.addEventListener('beforeunload', () => {
            if (this.isDirty) {
                this.saveSessions();
            }
        });
    }

    /**
     * Save sessions to storage (optimized - no backup to save space)
     */
    private saveSessions(): void {
        // Prevent saving while pruning to avoid recursion
        if (this.isPruning) {
            return;
        }

        try {

            // OPTIMIZATION: Sanitize messages before saving to reduce size
            const sessionsArray = Array.from(this.sessions.values()).map(session => {
                // Ensure pinnedMessages is string[] for storage to match type definition strictness if needed
                // But types.ts says Message[] | string[]
                // We'll leave it as is but careful with the slice in prune
                return {
                    ...session,
                    messages: this.sanitizeMessages(session.messages)
                };
            });

            const serialized = JSON.stringify(sessionsArray);

            // Proactive size check on every save — localStorage is shared and limited (~5MB)
            const sizeInMB = serialized.length / (1024 * 1024);
            if (sizeInMB > 1) {
                console.warn(`[SessionManager] Sessions too large (~${sizeInMB.toFixed(2)} MB), pruning...`);
                this.pruneOldSessions();
                return;
            }

            // Save to main storage (NO BACKUP - saves 50% space)
            localStorage.setItem(this.STORAGE_KEY, serialized);

            // Remove old backup if exists (cleanup)
            localStorage.removeItem(this.BACKUP_KEY);

            // Notify callbacks
            this.saveCallbacks.forEach(cb => cb(sessionsArray));
        } catch (error) {
            console.error('[SessionManager] Failed to save sessions:', error);

            // Try to save with reduced data if quota exceeded (but prevent infinite loop)
            if (error instanceof DOMException && error.name === 'QuotaExceededError' && !this.isPruning) {
                console.warn('[SessionManager] Storage quota exceeded, attempting to prune old sessions');
                this.pruneOldSessions();
            } else if (this.isPruning) {
                console.error('[SessionManager] CRITICAL: Still exceeding quota after pruning. Clearing oldest messages.');
                this.emergencyPrune();
            }
        }
    }


    /**
     * Helper to get safe timestamp from session
     */
    private getSessionTimestamp(session: ChatSession): number {
        if (!session.lastModified) return 0;
        if (typeof session.lastModified === 'number') return session.lastModified;
        if (session.lastModified instanceof Date) return session.lastModified.getTime();
        return new Date(session.lastModified).getTime();
    }

    /**
     * Prune old sessions to free up space - AGGRESSIVE
     */
    private pruneOldSessions(): void {
        if (this.isPruning) {
            return;
        }

        this.isPruning = true;
        console.log('[SessionManager] Pruning sessions to reduce storage...');

        try {
            const sessionsArray = Array.from(this.sessions.values());
            const sorted = sessionsArray.sort((a, b) => {
                return this.getSessionTimestamp(b) - this.getSessionTimestamp(a);
            });

            // AGGRESSIVE: Keep only 5 most recent sessions (was 10)
            const toKeep = sorted.slice(0, 5).map(session => {
                // Sanitize and trim messages
                const trimmedMessages = this.sanitizeMessages(
                    session.messages.slice(-10) // Keep only last 10 messages per session (was 15)
                );

                return {
                    ...session,
                    messages: trimmedMessages,
                    // Remove large optional data
                    generatedMaterials: (session.generatedMaterials || []).slice(0, 1), // Only keep 1 material
                    pinnedMessages: (session.pinnedMessages || []) as any // Cast to any to avoid complex union type error
                };
            });

            this.sessions.clear();
            toKeep.forEach(session => {
                this.sessions.set(session.id, session);
            });

            // Try to save after pruning
            try {
                const serialized = JSON.stringify(toKeep);
                localStorage.setItem(this.STORAGE_KEY, serialized);
                localStorage.removeItem(this.BACKUP_KEY); // Clean up backup
                console.log(`[SessionManager] Pruned to ${toKeep.length} sessions`);
            } catch (saveError) {
                console.error('[SessionManager] Prune save failed:', saveError);
                this.emergencyPrune();
            }
        } finally {
            this.isPruning = false;
        }
    }

    /**
     * Emergency prune - last resort when regular pruning fails
     * Keeps only the current session with truncated messages
     */
    private emergencyPrune(): void {
        console.error('[SessionManager] EMERGENCY PRUNE - Keeping only current session');

        try {
            if (this.currentSessionId) {
                const currentSession = this.sessions.get(this.currentSessionId);
                if (currentSession) {
                    // Keep only last 10 messages and truncate very long content
                    const truncatedSession = {
                        ...currentSession,
                        messages: currentSession.messages.slice(-10).map(msg => ({
                            ...msg,
                            content: msg.content.length > 5000
                                ? msg.content.substring(0, 5000) + '... [truncated]'
                                : msg.content
                        }))
                    };

                    this.sessions.clear();
                    this.sessions.set(currentSession.id, truncatedSession);

                    try {
                        const serialized = JSON.stringify([truncatedSession]);
                        localStorage.setItem(this.STORAGE_KEY, serialized);
                        console.log('[SessionManager] Emergency prune completed - saved current session only');
                    } catch {
                        // Last resort: nuke sessions entirely
                        localStorage.removeItem(this.STORAGE_KEY);
                        console.error('[SessionManager] Emergency prune still failed — cleared storage key');
                    }
                } else {
                    // No current session, clear everything
                    this.sessions.clear();
                    localStorage.removeItem(this.STORAGE_KEY);
                    console.log('[SessionManager] Emergency prune completed - cleared all sessions');
                }
            } else {
                // Keep only the most recent session
                const sorted = Array.from(this.sessions.values())
                    .sort((a, b) => this.getSessionTimestamp(b) - this.getSessionTimestamp(a));

                if (sorted.length > 0) {
                    const latest = {
                        ...sorted[0],
                        messages: sorted[0].messages.slice(-10).map(msg => ({
                            ...msg,
                            content: msg.content.length > 5000
                                ? msg.content.substring(0, 5000) + '... [truncated]'
                                : msg.content
                        }))
                    };

                    this.sessions.clear();
                    this.sessions.set(latest.id, latest);
                    try {
                        localStorage.setItem(this.STORAGE_KEY, JSON.stringify([latest]));
                        console.log('[SessionManager] Emergency prune completed - saved most recent session');
                    } catch {
                        localStorage.removeItem(this.STORAGE_KEY);
                        console.error('[SessionManager] Emergency prune still failed — cleared storage key');
                    }
                } else {
                    this.sessions.clear();
                    localStorage.removeItem(this.STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error('[SessionManager] Emergency prune failed:', error);
            // Ultimate fallback - clear everything
            this.sessions.clear();
            try {
                localStorage.removeItem(this.STORAGE_KEY);
                console.log('[SessionManager] Cleared all session data as last resort');
            } catch (e) {
                console.error('[SessionManager] Cannot clear localStorage:', e);
            }
        }
    }

    /**
     * Mark sessions as dirty (needs save)
     */
    private markDirty(): void {
        this.isDirty = true;
        this.cachedSessionsArray = null; // Invalidate cache
    }

    /**
     * Get all sessions (cached for performance)
     */
    public getAllSessions(): ChatSession[] {
        if (!this.cachedSessionsArray) {
            this.cachedSessionsArray = Array.from(this.sessions.values());
        }
        return this.cachedSessionsArray;
    }

    /**
     * Get sessions for a specific user
     */
    public getUserSessions(userId: string | undefined): ChatSession[] {
        return this.getAllSessions().filter(s => {
            if (userId) {
                return s.userId === userId;
            } else {
                return !s.userId; // Guest sessions
            }
        });
    }

    /**
     * Get session by ID
     */
    public getSession(sessionId: string): ChatSession | undefined {
        return this.sessions.get(sessionId);
    }

    /**
     * Create new session
     */
    public createSession(userId?: string): ChatSession {
        const sessionId = Date.now().toString();
        const newSession: ChatSession = {
            id: sessionId,
            userId: userId,
            title: 'New Chat',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            lastModified: Date.now(),
            pinned: false,
            // Initialize arrays for session data
            pinnedMessages: [],
            generatedMaterials: [],
            tags: []
        };

        this.sessions.set(sessionId, newSession);
        this.currentSessionId = sessionId;
        this.markDirty();

        return newSession;
    }

    /**
     * Create a fresh guest session (always new on page load)
     * Users always start with a clean slate
     */
    public getOrCreateGuestSession(): ChatSession {
        // ALWAYS create a new session - don't force users into old sessions
        const newSession = this.createSession(undefined);
        localStorage.setItem(this.GUEST_SESSION_KEY, newSession.id);
        return newSession;
    }

    /**
     * Get the stored guest session ID (if any)
     */
    public getStoredGuestSessionId(): string | null {
        return localStorage.getItem(this.GUEST_SESSION_KEY);
    }

    /**
     * Clear the guest session ID (e.g., when user creates account)
     */
    public clearGuestSessionId(): void {
        localStorage.removeItem(this.GUEST_SESSION_KEY);
        console.log('[SessionManager] Cleared guest session ID');
    }

    /**
     * Migrate guest session to a user account
     */
    public migrateGuestSessionToUser(userId: string): ChatSession | null {
        const guestSessionId = localStorage.getItem(this.GUEST_SESSION_KEY);
        if (!guestSessionId) return null;

        const guestSession = this.sessions.get(guestSessionId);
        if (!guestSession) {
            this.clearGuestSessionId();
            return null;
        }

        // Update the session with the new user ID
        const migratedSession: ChatSession = {
            ...guestSession,
            userId: userId,
            lastModified: Date.now()
        };

        this.sessions.set(guestSessionId, migratedSession);
        this.markDirty();

        // Clear the guest session ID since it's now a user session
        this.clearGuestSessionId();

        console.log('[SessionManager] Migrated guest session to user:', userId);

        return migratedSession;
    }

    // Debounce tracking for truncation warnings
    private truncationWarnings = new Map<string, number>();

    /**
     * Sanitize messages to prevent localStorage quota issues
     * Truncates very long message content
     * Skips truncation for messages still streaming
     * 
     * INCREASED LIMIT: 100k chars to prevent premature truncation
     */
    private sanitizeMessages(messages: Message[]): Message[] {
        // OPTIMIZED: More aggressive size limits
        const MAX_MESSAGE_LENGTH = 30000; // 30k chars per message (reduced from 100k)
        const MAX_ATTACHMENT_SIZE = 10000; // 10k chars for attachment data

        return messages.map(msg => {
            // Skip truncation for messages still streaming
            if ((msg as any).isStreaming) {
                return msg;
            }

            let sanitized = { ...msg };

            // Truncate long content
            if (msg.content.length > MAX_MESSAGE_LENGTH) {
                sanitized.content = msg.content.substring(0, MAX_MESSAGE_LENGTH) + '\n\n... [Truncated for storage]';
            }

            // Truncate long thought process
            if (msg.thought && msg.thought.length > 5000) {
                sanitized.thought = msg.thought.substring(0, 5000) + '...';
            }

            // Strip large attachment data (keep metadata only)
            if (msg.attachments && msg.attachments.length > 0) {
                sanitized.attachments = msg.attachments.map(att => {
                    if (att.data && att.data.length > MAX_ATTACHMENT_SIZE) {
                        // Keep only metadata, not the actual data
                        return {
                            ...att,
                            data: '', // Strip data for storage
                            previewUrl: undefined // Strip preview
                        };
                    }
                    return att;
                });
            }

            return sanitized;
        });
    }

    /**
     * Update session
     */
    public updateSession(sessionId: string, updates: Partial<ChatSession>): void {
        const session = this.sessions.get(sessionId);
        if (!session) {
            console.warn('[SessionManager] Session not found:', sessionId);
            return;
        }

        // CRITICAL FIX: Only include messages in updates if they're actually provided
        // This prevents wiping messages when just updating title/isPinned
        const sanitizedUpdates: Partial<ChatSession> = { ...updates };

        if (updates.messages) {
            sanitizedUpdates.messages = this.sanitizeMessages(updates.messages);
        } else {
            // Don't include messages field at all if not updating
            delete sanitizedUpdates.messages;
        }

        const updated = {
            ...session,
            ...sanitizedUpdates,
            lastModified: Date.now()
        };

        this.sessions.set(sessionId, updated);
        this.markDirty();

        console.log(`[SessionManager] Updated session ${sessionId}: ${Object.keys(updates).join(', ')}`);

        // Queue for Firebase sync if online
        if (connectionManager.isOnline()) {
            connectionManager.queueRequest({
                type: 'session',
                data: updated,
                priority: 3
            });
        }
    }

    /**
     * Add message to session
     */
    public addMessage(sessionId: string, message: Message): void {
        const session = this.sessions.get(sessionId);
        if (!session) {
            console.warn('[SessionManager] Session not found:', sessionId);
            return;
        }

        session.messages.push(message);
        session.lastModified = Date.now();

        // Auto-generate title from first user message if needed
        if (session.messages.length === 1 && session.title === 'New Chat') {
            session.title = this.generateSmartTitle(message.content);
        }

        this.sessions.set(sessionId, session);
        this.markDirty();

        // Queue for Firebase sync
        if (connectionManager.isOnline()) {
            connectionManager.queueRequest({
                type: 'session',
                data: session,
                priority: 4
            });
        }
    }

    /**
     * Generate a smart title from message content
     * Extracts the core question/topic instead of dumb truncation
     */
    private generateSmartTitle(content: string): string {
        // Clean up markdown, extra whitespace, etc.
        let text = content
            .replace(/```[\s\S]*?```/g, '') // Remove code blocks
            .replace(/[#*`_~\[\]()]/g, '')  // Remove markdown chars
            .replace(/\n+/g, ' ')           // Newlines to spaces
            .replace(/\s+/g, ' ')           // Collapse whitespace
            .trim();

        if (!text) return 'New Chat';

        // If it's a question, try to keep the full question up to a reasonable length
        const questionMatch = text.match(/^(.+?\?)/);
        if (questionMatch && questionMatch[1].length <= 60) {
            return questionMatch[1];
        }

        // Try to find a natural break point (sentence end, comma, dash)
        if (text.length > 50) {
            // Look for sentence boundary within 40-60 chars
            const sentenceEnd = text.substring(0, 60).search(/[.!?]\s/);
            if (sentenceEnd > 15) {
                return text.substring(0, sentenceEnd + 1);
            }

            // Look for comma or dash break
            const commaBreak = text.substring(30, 55).indexOf(',');
            if (commaBreak > 0) {
                return text.substring(0, 30 + commaBreak);
            }

            // Last resort: break at word boundary near 45 chars
            const wordBreak = text.substring(0, 50).lastIndexOf(' ');
            if (wordBreak > 20) {
                return text.substring(0, wordBreak);
            }
        }

        // Short enough to use as-is
        return text.length > 55 ? text.substring(0, 52) + '...' : text;
    }

    /**
     * Update messages in session
     */
    public updateMessages(sessionId: string, messages: Message[]): void {
        const session = this.sessions.get(sessionId);
        if (!session) {
            console.warn('[SessionManager] Session not found:', sessionId);
            return;
        }

        session.messages = messages;
        session.lastModified = Date.now();

        this.sessions.set(sessionId, session);
        this.markDirty();
    }

    /**
     * Delete session
     */
    public deleteSession(sessionId: string): void {
        if (this.sessions.delete(sessionId)) {
            this.markDirty();

            if (this.currentSessionId === sessionId) {
                this.currentSessionId = null;
            }

            console.log('[SessionManager] Deleted session:', sessionId);

            // Queue deletion for Firebase
            if (connectionManager.isOnline()) {
                connectionManager.queueRequest({
                    type: 'session',
                    data: { id: sessionId, _deleted: true },
                    priority: 2
                });
            }
        }
    }

    /**
     * Rename session
     */
    public renameSession(sessionId: string, newTitle: string): void {
        this.updateSession(sessionId, { title: newTitle });
    }

    /**
     * Pin/unpin session
     */
    public pinSession(sessionId: string, isPinned: boolean): void {
        this.updateSession(sessionId, { pinned: isPinned });
    }

    /**
     * Toggle pin on a message within a session
     */
    public toggleMessagePin(sessionId: string, messageId: string): boolean {
        const session = this.sessions.get(sessionId);
        if (!session) return false;

        const pinnedMessages = (session.pinnedMessages || []) as string[];
        const isPinned = pinnedMessages.includes(messageId);

        const updatedPinnedMessages = isPinned
            ? pinnedMessages.filter(id => id !== messageId)
            : [...pinnedMessages, messageId];

        session.pinnedMessages = updatedPinnedMessages;
        session.lastModified = Date.now();

        this.sessions.set(sessionId, session);
        this.markDirty();
        this.saveSessions(); // Force immediate save for pin actions

        console.log(`[SessionManager] ${isPinned ? 'Unpinned' : 'Pinned'} message ${messageId} in session ${sessionId}`);
        return !isPinned;
    }

    /**
     * Add generated material to a session
     */
    public addGeneratedMaterial(sessionId: string, material: any): void {
        const session = this.sessions.get(sessionId);
        if (!session) return;

        const materials = session.generatedMaterials || [];
        session.generatedMaterials = [...materials, material];
        session.lastModified = Date.now();

        this.sessions.set(sessionId, session);
        this.markDirty();
        this.saveSessions(); // Force immediate save for material additions

        console.log(`[SessionManager] Added material to session ${sessionId}:`, material.title);
    }

    /**
     * Remove generated material from a session
     */
    public removeGeneratedMaterial(sessionId: string, materialId: string): void {
        const session = this.sessions.get(sessionId);
        if (!session || !session.generatedMaterials) return;

        session.generatedMaterials = session.generatedMaterials.filter(m => m.id !== materialId);
        session.lastModified = Date.now();

        this.sessions.set(sessionId, session);
        this.markDirty();
        this.saveSessions(); // Force immediate save

        console.log(`[SessionManager] Removed material ${materialId} from session ${sessionId}`);
    }

    /**
     * Get pinned messages for a session
     */
    public getPinnedMessages(sessionId: string): string[] {
        const session = this.sessions.get(sessionId);
        return (session?.pinnedMessages || []) as string[];
    }

    /**
     * Get generated materials for a session
     */
    public getGeneratedMaterials(sessionId: string): any[] {
        const session = this.sessions.get(sessionId);
        return session?.generatedMaterials || [];
    }

    /**
     * Set current session
     */
    public setCurrentSession(sessionId: string | null): void {
        this.currentSessionId = sessionId;
    }

    /**
     * Get current session ID
     */
    public getCurrentSessionId(): string | null {
        return this.currentSessionId;
    }

    /**
     * Subscribe to session changes
     */
    public subscribe(callback: (sessions: ChatSession[]) => void): () => void {
        this.saveCallbacks.add(callback);
        // Immediately call with current sessions
        callback(this.getAllSessions());

        return () => {
            this.saveCallbacks.delete(callback);
        };
    }

    /**
     * Force immediate save
     */
    public forceSave(): void {
        this.saveSessions();
        this.isDirty = false;
    }

    // Debounce tracking for import logging
    private lastImportLog = 0;

    /**
     * Import sessions (merge with existing)
     * Does NOT mark dirty since this is typically called when syncing from Firebase
     */
    public importSessions(sessions: ChatSession[], markAsDirty = false): void {
        sessions.forEach(session => {
            const hydrated = this.hydrateSession(session);
            this.sessions.set(hydrated.id, hydrated);
        });

        // Only mark dirty if explicitly requested (e.g., when restoring from backup)
        if (markAsDirty) {
            this.markDirty();
        }

        // Debounce import logging - only log once per 10 seconds
        const now = Date.now();
        if (now - this.lastImportLog > 10000) {
            console.log(`[SessionManager] Imported ${sessions.length} sessions`);
            this.lastImportLog = now;
        }
    }

    /**
     * Clear all sessions
     */
    public clearAll(): void {
        this.sessions.clear();
        this.currentSessionId = null;
        this.saveSessions();
    }

    /**
     * Schedule periodic cleanup of old guest sessions
     */
    private scheduleGuestSessionCleanup(): void {
        // Run cleanup every hour
        setInterval(() => {
            this.cleanupOldGuestSessions();
        }, 60 * 60 * 1000);

        // Also run on startup after a delay
        setTimeout(() => {
            this.cleanupOldGuestSessions();
        }, 10000);
    }

    /**
     * Setup auto-cleanup for old sessions
     * Only deletes truly empty sessions and trims very long ones
     */
    private setupAutoCleanup(): void {
        // Run cleanup every 30 minutes (was 5 minutes)
        this.autoCleanupInterval = setInterval(() => {
            this.cleanupInactiveSessions();
            this.deleteEmptySessions();
        }, 30 * 60 * 1000);

        // Run cleanup on startup after 60 seconds
        setTimeout(() => {
            this.cleanupInactiveSessions();
            this.deleteEmptySessions();
        }, 60000);
    }

    /**
     * Cleanup inactive sessions - much less aggressive than before
     * Only deletes truly empty sessions (no messages) older than 24h
     * Trims sessions that exceed max message count
     * NEVER merges sessions or deletes sessions with messages
     */
    public cleanupInactiveSessions(): void {
        const now = Date.now();
        let cleanedCount = 0;
        let trimmedCount = 0;

        for (const session of this.sessions.values()) {
            const isCurrent = session.id === this.currentSessionId;
            const isPinned = session.pinned;

            // Only delete truly empty sessions (0 messages) that are older than 24 hours
            if (session.messages.length === 0 && !isCurrent && !isPinned) {
                const age = now - this.getSessionTimestamp(session);
                if (age > 24 * 60 * 60 * 1000) {
                    this.sessions.delete(session.id);
                    cleanedCount++;
                    continue;
                }
            }

            // Trim sessions that have too many messages (performance only)
            if (session.messages.length > this.MAX_MESSAGES_PER_SESSION) {
                const trimmed = this.trimSessionMessages(session);
                this.sessions.set(session.id, trimmed);
                trimmedCount++;
            }
        }

        if (cleanedCount > 0 || trimmedCount > 0) {
            this.markDirty();
            console.log(`[SessionManager] Auto-cleanup: deleted=${cleanedCount}, trimmed=${trimmedCount}`);
        }
    }

    /**
     * Find a session to merge the inactive session into
     */
    private findMergeCandidate(session: ChatSession, candidates: ChatSession[]): ChatSession | null {
        // Only merge guest sessions with other guest sessions
        // Don't merge user sessions to prevent data confusion
        if (session.userId) return null;

        for (const candidate of candidates) {
            if (candidate.userId !== session.userId) continue;
            if (candidate.id === session.id) continue;

            // Check if titles are similar (basic similarity check)
            const titleA = session.title.toLowerCase().replace(/[^a-z0-9]/g, '');
            const titleB = candidate.title.toLowerCase().replace(/[^a-z0-9]/g, '');

            if (titleA === 'newchat' || titleB === 'newchat') {
                // Merge "New Chat" sessions into existing sessions
                return candidate;
            }

            // Check if first message content is similar
            if (session.messages[0] && candidate.messages[0]) {
                const contentA = session.messages[0].content.toLowerCase().slice(0, 50);
                const contentB = candidate.messages[0].content.toLowerCase().slice(0, 50);

                if (contentA === contentB) {
                    return candidate;
                }
            }
        }

        return null;
    }

    /**
     * Merge source session into target session
     */
    private mergeSessions(target: ChatSession, source: ChatSession): void {
        // Add source messages to target, keeping chronological order
        const allMessages = [...target.messages, ...source.messages]
            .sort((a, b) => {
                const timeA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp.getTime();
                const timeB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp.getTime();
                return timeA - timeB;
            });

        // Deduplicate by id
        const seen = new Set<string>();
        const uniqueMessages = allMessages.filter(msg => {
            if (seen.has(msg.id)) return false;
            seen.add(msg.id);
            return true;
        });

        // Limit total messages
        const limitedMessages = uniqueMessages.slice(-this.MAX_MESSAGES_PER_SESSION);

        target.messages = limitedMessages;
        target.lastModified = Date.now();

        // Merge pinned messages
        if (source.pinnedMessages?.length) {
            const targetPinned = (target.pinnedMessages || []) as string[];
            const sourcePinned = source.pinnedMessages as string[];

            target.pinnedMessages = [...new Set([
                ...targetPinned,
                ...sourcePinned
            ])];
        }

        // Merge generated materials
        if (source.generatedMaterials?.length) {
            target.generatedMaterials = [
                ...(target.generatedMaterials || []),
                ...source.generatedMaterials
            ].slice(0, 10); // Limit materials
        }

        this.sessions.set(target.id, target);
        console.log(`[SessionManager] Merged session ${source.id} into ${target.id}`);
    }

    /**
     * Trim session messages to keep only recent ones
     */
    private trimSessionMessages(session: ChatSession): ChatSession {
        // Keep first 5 messages (context) + last 50 messages
        const messages = session.messages;
        const keepFirst = 5;
        const keepLast = 50;

        if (messages.length <= keepFirst + keepLast) {
            return session;
        }

        const trimmed = [
            ...messages.slice(0, keepFirst),
            ...messages.slice(-keepLast)
        ];

        return {
            ...session,
            messages: this.sanitizeMessages(trimmed),
            lastModified: Date.now()
        };
    }

    /**
     * Force cleanup of a specific session's messages (for performance)
     */
    public trimSession(sessionId: string, maxMessages: number = 50): void {
        const session = this.sessions.get(sessionId);
        if (!session) return;

        if (session.messages.length > maxMessages) {
            // Keep first 3 messages for context, rest are recent
            const keepFirst = 3;
            const keepRecent = maxMessages - keepFirst;

            const trimmedMessages = [
                ...session.messages.slice(0, keepFirst),
                ...session.messages.slice(-keepRecent)
            ];

            session.messages = this.sanitizeMessages(trimmedMessages);
            session.lastModified = Date.now();
            this.sessions.set(sessionId, session);
            this.markDirty();

            console.log(`[SessionManager] Trimmed session ${sessionId} to ${session.messages.length} messages`);
        }
    }

    /**
     * Get session message count (for UI indicators)
     */
    public getSessionMessageCount(sessionId: string): number {
        const session = this.sessions.get(sessionId);
        return session?.messages.length || 0;
    }

    /**
     * Check if session is "large" and may cause performance issues
     */
    public isLargeSession(sessionId: string): boolean {
        const count = this.getSessionMessageCount(sessionId);
        return count > 50;
    }

    /**
     * Check if a session has meaningful conversation
     * Returns false if session only has system messages or very short/empty messages
     * STRICTER: Requires substantive questions, not just greetings
     */
    private hasMeaningfulContent(session: ChatSession): boolean {
        // Empty session
        if (session.messages.length === 0) {
            return false;
        }

        // Session with only 1-2 messages (just a greeting and response)
        if (session.messages.length <= 2) {
            return false;
        }

        // Check for meaningful user messages (not system messages)
        const userMessages = session.messages.filter(m => m.role === Role.USER);
        if (userMessages.length === 0) {
            return false;
        }

        // Check if user messages are substantive (more than just greetings)
        const substantiveMessages = userMessages.filter(m => {
            const content = m.content.trim().toLowerCase();
            // Ignore very short messages (less than 10 chars)
            if (content.length < 10) return false;
            // Extended list of common greetings and short phrases
            const greetings = [
                'hi', 'hello', 'hey', 'test', 'ok', 'okay', 'thanks', 'thank you',
                'bye', 'goodbye', 'hi luca', 'hello luca', 'hey luca', 'hi there',
                'good morning', 'good afternoon', 'good evening', 'what can you do',
                'who are you', 'testing', 'test 123', 'asdf', 'help', 'yes', 'no'
            ];
            if (greetings.includes(content)) return false;
            // Short messages starting with greeting are not substantive
            if (content.length < 20 && greetings.some(g => content.startsWith(g))) return false;
            return true;
        });

        return substantiveMessages.length >= 1;
    }

    /**
     * Auto-delete sessions with no meaningful content
     * This runs periodically to clean up sessions that users started but didn't use
     * MORE AGGRESSIVE: 2-minute timeout instead of 10
     */
    public deleteEmptySessions(): void {
        const now = Date.now();
        const TWO_MINUTES = 2 * 60 * 1000; // Reduced from 10 minutes
        const currentSessionId = this.getCurrentSessionId();
        let deletedCount = 0;

        for (const [sessionId, session] of this.sessions) {
            // Don't delete current active session
            if (sessionId === currentSessionId) continue;

            // Don't delete pinned sessions
            if (session.pinned) continue;

            // Check age and meaningful content
            const age = now - this.getSessionTimestamp(session);
            const isMeaningful = this.hasMeaningfulContent(session);

            // Delete sessions older than 2 minutes with no meaningful content
            if (age > TWO_MINUTES && !isMeaningful) {
                this.sessions.delete(sessionId);
                deletedCount++;
                console.log(`[SessionManager] Auto-deleted empty session ${sessionId} (age: ${Math.round(age / 60000)}m)`);
            }
        }

        if (deletedCount > 0) {
            this.markDirty();
            console.log(`[SessionManager] Auto-deleted ${deletedCount} empty sessions`);
        }
    }

    /**
     * Clean up old guest sessions that are:
     * - Older than 7 days
     * - Have no messages (empty sessions older than 1 day)
     * - Not the current guest session
     */
    public cleanupOldGuestSessions(): void {
        const now = Date.now();
        const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
        const ONE_DAY = 24 * 60 * 60 * 1000;
        const currentGuestSessionId = this.getStoredGuestSessionId();

        let cleanedCount = 0;

        for (const [sessionId, session] of this.sessions) {
            // Skip non-guest sessions (sessions with userId)
            if (session.userId) continue;

            // Skip the current guest session
            if (sessionId === currentGuestSessionId) continue;

            // Handle createdAt potentially being string or number
            const createdAt = typeof session.createdAt === 'string'
                ? new Date(session.createdAt).getTime()
                : session.createdAt;
            const age = now - createdAt;
            const isEmpty = session.messages.length === 0;

            // Remove empty guest sessions older than 1 day
            if (isEmpty && age > ONE_DAY) {
                this.sessions.delete(sessionId);
                cleanedCount++;
                continue;
            }

            // Remove guest sessions older than 7 days
            if (age > SEVEN_DAYS) {
                this.sessions.delete(sessionId);
                cleanedCount++;
            }
        }

        if (cleanedCount > 0) {
            this.markDirty();
            this.saveSessions();
            console.log(`[SessionManager] Cleaned up ${cleanedCount} old guest sessions`);
        }
    }

    /**
     * Get statistics about sessions
     */
    public getStats(): {
        totalSessions: number;
        userSessions: number;
        guestSessions: number;
        totalMessages: number;
    } {
        let userSessions = 0;
        let guestSessions = 0;
        let totalMessages = 0;

        for (const session of this.sessions.values()) {
            if (session.userId) {
                userSessions++;
            } else {
                guestSessions++;
            }
            totalMessages += session.messages.length;
        }

        return {
            totalSessions: this.sessions.size,
            userSessions,
            guestSessions,
            totalMessages
        };
    }
}

// Export singleton instance
export const sessionManager = new SessionManager();
